/**
 * Dynamic-array functions of the Core_Formula_Set (Requirement 4.1).
 *
 * `UNIQUE`, `SORT`, and `FILTER` return a grid rather than a scalar. The
 * evaluator keeps that grid in {@link EvalResult.array} so the recalculation pass
 * can write it into a spill region below and to the right of the host cell,
 * while the host cell itself stores the top-left value.
 *
 * Comparison and equality follow the same rules as the operators (`values.ts`),
 * so text is matched case-insensitively and numbers sort before text before
 * booleans.
 *
 * `FILTER` with no matching rows yields `#N/A` unless the call supplies an
 * `if_empty` argument (Requirement 4.11); the error taxonomy of this project has
 * no `#CALC!`.
 */

import {
  arrayOf,
  arrayOfGrid,
  compareScalars,
  err,
  gridOf,
  isError,
  toBool,
  type EvalContext,
  type FnSpec,
  type Scalar,
  type Value,
} from '../values.js';
import { isFailure, optBoolArg, optIntArg } from './helpers.js';

/** Equality key for a scalar, consistent with {@link compareScalars}. */
function keyOf(cell: Scalar): string {
  switch (cell.k) {
    case 'empty':
      return 'e';
    case 'number':
      return `n:${cell.v}`;
    case 'text':
      return `t:${cell.v.toUpperCase()}`;
    case 'bool':
      return `b:${cell.v ? 1 : 0}`;
    case 'error':
      return `x:${cell.v}`;
  }
}

function keyOfRow(row: readonly Scalar[]): string {
  return row.map(keyOf).join('\u0000');
}

/** Materializes an argument as rows of scalars, or returns the size error. */
function rowsOf(value: Value | undefined, ctx: EvalContext): Scalar[][] | Scalar {
  if (value === undefined) return err('#VALUE!');
  const materialized = arrayOfGrid(gridOf(value, ctx));
  return materialized.k === 'array' ? materialized.cells : materialized;
}

function transpose(rows: readonly Scalar[][]): Scalar[][] {
  const height = rows.length;
  const width = rows[0]?.length ?? 0;
  const out: Scalar[][] = [];
  for (let c = 0; c < width; c++) {
    const row: Scalar[] = [];
    for (let r = 0; r < height; r++) row.push(rows[r]?.[c] ?? { k: 'empty' });
    out.push(row);
  }
  return out;
}

export const ARRAY_FUNCTIONS: readonly FnSpec[] = [
  {
    name: 'UNIQUE',
    min: 1,
    max: 3,
    array: true,
    errorTolerant: true,
    fn: (args, ctx) => {
      const rows = rowsOf(args[0], ctx);
      if (!Array.isArray(rows)) return rows;
      const byCol = optBoolArg(args, 1, ctx, false);
      if (isFailure(byCol)) return byCol.fail;
      const exactlyOnce = optBoolArg(args, 2, ctx, false);
      if (isFailure(exactlyOnce)) return exactlyOnce.fail;

      const oriented = byCol ? transpose(rows) : rows;
      const counts = new Map<string, number>();
      for (const row of oriented) {
        const key = keyOfRow(row);
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
      const seen = new Set<string>();
      const kept: Scalar[][] = [];
      for (const row of oriented) {
        const key = keyOfRow(row);
        if (seen.has(key)) continue;
        seen.add(key);
        if (exactlyOnce && (counts.get(key) ?? 0) !== 1) continue;
        kept.push(row);
      }
      if (kept.length === 0) return err('#N/A');
      return arrayOf(byCol ? transpose(kept) : kept);
    },
  },
  {
    name: 'SORT',
    min: 1,
    max: 4,
    array: true,
    errorTolerant: true,
    fn: (args, ctx) => {
      const rows = rowsOf(args[0], ctx);
      if (!Array.isArray(rows)) return rows;
      const index = optIntArg(args, 1, ctx, 1);
      if (isFailure(index)) return index.fail;
      const order = optIntArg(args, 2, ctx, 1);
      if (isFailure(order)) return order.fail;
      const byCol = optBoolArg(args, 3, ctx, false);
      if (isFailure(byCol)) return byCol.fail;
      if (order !== 1 && order !== -1) return err('#VALUE!');

      const oriented = byCol ? transpose(rows) : rows;
      const width = oriented[0]?.length ?? 0;
      if (index < 1 || index > width) return err('#VALUE!');
      const direction = order;
      const sorted = oriented
        .map((row, position) => ({ row, position }))
        .sort((a, b) => {
          const left = a.row[index - 1] ?? { k: 'empty' as const };
          const right = b.row[index - 1] ?? { k: 'empty' as const };
          const cmp = compareScalars(left, right) * direction;
          // Stable: equal keys keep their original order.
          return cmp !== 0 ? cmp : a.position - b.position;
        })
        .map((entry) => entry.row);
      return arrayOf(byCol ? transpose(sorted) : sorted);
    },
  },
  {
    name: 'FILTER',
    min: 2,
    max: 3,
    array: true,
    errorTolerant: true,
    fn: (args, ctx) => {
      const rows = rowsOf(args[0], ctx);
      if (!Array.isArray(rows)) return rows;
      const includeGrid = gridOf(args[1] as Value, ctx);
      const height = rows.length;
      const width = rows[0]?.length ?? 0;

      // The include mask is a column vector (row filter) or a row vector
      // (column filter); anything else cannot be aligned.
      const byRow = includeGrid.cols === 1 && includeGrid.rows === height;
      const byColumn = includeGrid.rows === 1 && includeGrid.cols === width;
      if (!byRow && !byColumn) return err('#VALUE!');

      const keep = (i: number): boolean | Scalar => {
        const cell = byRow ? includeGrid.get(i, 0) : includeGrid.get(0, i);
        if (isError(cell)) return cell;
        const b = toBool(cell);
        return typeof b === 'boolean' ? b : err(b);
      };

      const kept: Scalar[][] = [];
      if (byRow) {
        for (let r = 0; r < height; r++) {
          const decision = keep(r);
          if (typeof decision !== 'boolean') return decision;
          if (decision) kept.push(rows[r] as Scalar[]);
        }
      } else {
        const columns: number[] = [];
        for (let c = 0; c < width; c++) {
          const decision = keep(c);
          if (typeof decision !== 'boolean') return decision;
          if (decision) columns.push(c);
        }
        for (const row of rows) kept.push(columns.map((c) => row[c] ?? { k: 'empty' as const }));
        if (columns.length === 0) kept.length = 0;
      }

      if (kept.length === 0 || (kept[0]?.length ?? 0) === 0) {
        const fallback = args[2];
        return fallback === undefined ? err('#N/A') : fallback;
      }
      return arrayOf(kept);
    },
  },
];
